import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Box, Typography } from '@mui/material'
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { ChartDataPoint, ChartSeries, QueryMode } from '../types'
import { CustomTooltip } from './ChartTooltips'
import { CHART_DIMENSIONS } from '../constants'
import { useStorePerformanceContext } from '../context/StorePerformanceContext'

interface BarLineChartProps {
  data: ChartDataPoint[]
  series: ChartSeries[]
  colors: (index: number) => string
  chartType: 'bar' | 'line'
  queryMode: QueryMode
  totalResultCount: number
  selectedResultIds: string[]
}

const BarLineChart = ({
  data,
  series,
  colors,
  chartType,
  queryMode,
  totalResultCount,
  selectedResultIds,
}: BarLineChartProps) => {
  const { colors: themeColors, t } = useStorePerformanceContext()
  const [portalEl, setPortalEl] = useState<HTMLDivElement | null>(null)
  const mousePosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })

  useEffect(() => {
    const div = document.createElement('div')
    div.id = 'chart-tooltip-portal'
    document.body.appendChild(div)
    setPortalEl(div)
    return () => {
      document.body.removeChild(div)
    }
  }, [])

  const PortalTooltip = (tooltipProps: any) => {
    if (!portalEl) return null

    const x = mousePosRef.current.x - 20
    const y = mousePosRef.current.y - 10

    return createPortal(
      <Box sx={{
        position: 'fixed',
        left: x,
        top: y,
        transform: 'translate(-100%, -100%)',
        zIndex: 9999,
        pointerEvents: 'none',
      }}>
        <CustomTooltip {...tooltipProps} />
      </Box>,
      portalEl
    )
  }

  if (!data.length || !series.length) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: '14px', color: themeColors.color9 }}>
          {t('general.ninformationNotFound')}
        </Typography>
      </Box>
    )
  }

  const chartWidth = (() => {
    if (chartType === 'bar') {
      const barsPerWeek = series.length
      const barCategoryGap = CHART_DIMENSIONS.BAR_CATEGORY_GAP
      const MAX_CHART_WIDTH = CHART_DIMENSIONS.MAX_WIDTH
      const MAX_BAR_WIDTH = CHART_DIMENSIONS.MAX_BAR_WIDTH
      const MIN_BAR_WIDTH = CHART_DIMENSIONS.MIN_BAR_WIDTH
      const typicalContainerWidth = CHART_DIMENSIONS.TYPICAL_CONTAINER_WIDTH
      
      const availableSpacePerBar = typicalContainerWidth / data.length
      const barWidthWith100Percent = (availableSpacePerBar * (1 - barCategoryGap)) / barsPerWeek
      
      if (barWidthWith100Percent >= 8) {
        return '100%'
      }
      
      const widthPerWeekWith24px = (barsPerWeek * MAX_BAR_WIDTH) / (1 - barCategoryGap) + 30
      const wouldBeWidth = data.length * widthPerWeekWith24px + 40
      
      if (wouldBeWidth <= MAX_CHART_WIDTH) {
        return wouldBeWidth
      }
      
      const availableForWeeks = MAX_CHART_WIDTH - 40
      const widthPerWeek = availableForWeeks / data.length
      const widthForBars = widthPerWeek - 30
      const calculatedBarWidth = (widthForBars * (1 - barCategoryGap)) / barsPerWeek
      const adaptiveBarWidth = Math.max(MIN_BAR_WIDTH, Math.min(calculatedBarWidth, MAX_BAR_WIDTH))
      const finalWidthPerWeek = (barsPerWeek * adaptiveBarWidth) / (1 - barCategoryGap) + 30
      return data.length * finalWidthPerWeek + 40
    }
    
    return '100%'
  })()

  const showLegend = series.length >= 2 && series.length <= 8

  return (
    <Box 
      onMouseMove={(e) => { mousePosRef.current = { x: e.clientX, y: e.clientY } }}
      sx={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ width: chartWidth, flex: 1, minHeight: 0, ...(chartType === 'line' ? { overflowX: 'hidden' } : {}) }}>
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'line' ? (
              <AreaChart data={data} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="week"
                  tick={{ fontSize: 12, fill: themeColors.color13 }}
                  axisLine={{ stroke: themeColors.color10 }}
                  padding={{ left: 20, right: 20 }}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 12, fill: themeColors.color13 }}
                  axisLine={{ stroke: themeColors.color10 }}
                />
                <Tooltip content={<PortalTooltip queryMode={queryMode} data={data} series={series} totalResultCount={totalResultCount} selectedResultIds={selectedResultIds} t={t} themeColors={themeColors} />} />
                {series.map((s, i) => (
                  <Area
                    key={s.id}
                    type="natural"
                    dataKey={s.id}
                    name={s.name}
                    stroke={colors(i)}
                    fill={colors(i)}
                    fillOpacity={0.5}
                    strokeWidth={2}
                    dot={{ fill: colors(i), r: 4, strokeWidth: 0 }}
                    activeDot={{ r: 7, strokeWidth: 0 }}
                    connectNulls={true}
                  />
                ))}
              </AreaChart>
            ) : (
              <BarChart
                data={data}
                margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                barCategoryGap="10%"
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="week"
                  tick={{ fontSize: 12, fill: themeColors.color13 }}
                  axisLine={{ stroke: themeColors.color10 }}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 12, fill: themeColors.color13 }}
                  axisLine={{ stroke: themeColors.color10 }}
                />
                <Tooltip content={<PortalTooltip queryMode={queryMode} data={data} series={series} totalResultCount={totalResultCount} selectedResultIds={selectedResultIds} t={t} themeColors={themeColors} />} />
                {series.map((s, i) => (
                  <Bar
                    key={s.id}
                    dataKey={s.id}
                    name={s.name}
                    fill={colors(i)}
                    radius={[4, 4, 0, 0]}
                    barSize={24}
                  />
                ))}
              </BarChart>
            )}
          </ResponsiveContainer>
        </Box>
      
      {showLegend && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '20px',
            flexWrap: 'wrap',
            maxHeight: '56px',
            overflow: 'hidden',
            padding: '0 20px 8px',
          }}
        >
          {series.map((s, i) => (
            <Box
              key={s.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                maxWidth: '220px',
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: '14px',
                  height: '14px',
                  backgroundColor: colors(i),
                  borderRadius: '2px',
                  flexShrink: 0,
                }}
              />
              <Typography
                title={s.name}
                sx={{
                  fontSize: '13px',
                  color: themeColors.color13,
                  lineHeight: '1.2',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export { BarLineChart }
